import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plug } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Label = ({ children }) => (
  <label className="block text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-400 mb-1.5">
    {children}
  </label>
);
const inputCls =
  "w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none transition-colors duration-200";

export const MyMT5 = ({ status, onChange }) => {
  const [creds, setCreds] = useState({
    login: status?.saved_login || "",
    password: "",
    server: status?.saved_server || "",
  });
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);

  // Status arrives after mount, so prefill the saved login/server without touching what the
  // trader has typed (the component must never remount — that used to wipe the password).
  useEffect(() => {
    setCreds((c) => ({
      ...c,
      login: c.login || status?.saved_login || "",
      server: c.server || status?.saved_server || "",
    }));
  }, [status?.saved_login, status?.saved_server]);

  const ping = useCallback(async () => {
    try {
      const { data } = await api.get("/mt5/ea-health");
      setHealth(data);
    } catch {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    ping();
    const id = setInterval(ping, 10000);
    return () => clearInterval(id);
  }, [ping, status?.connected, status?.mode]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/mt5/connect", creds);
      toast.success("Your MT5 account is connected");
      await Promise.all([ping(), onChange?.()]);
    } catch (err) {
      toast.error(errMsg(err));
      ping();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/mt5/disconnect");
      toast.success("Disconnected");
      await Promise.all([ping(), onChange?.()]);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <form
      onSubmit={connect}
      data-testid="my-mt5-form"
      className="border border-zinc-800 bg-zinc-900/30 p-4 flex flex-col gap-3"
    >
      <div className="flex items-center gap-2">
        <Plug className="h-4 w-4 text-emerald-500" />
        <h3 className="text-sm font-heading font-semibold tracking-tight">My MT5 Account</h3>
        {health && (
          <span
            data-testid="ea-health-badge"
            title={health.error || health.mode}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] ${
              health.mode === "simulator"
                ? "border-zinc-700 text-zinc-300"
                : health.ea_online
                ? "border-emerald-700 text-emerald-400"
                : "border-red-800 text-red-400"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                health.mode === "simulator"
                  ? "bg-zinc-600"
                  : health.ea_online
                  ? "bg-emerald-400 animate-pulse"
                  : "bg-red-500"
              }`}
            />
            {health.mode === "simulator"
              ? "simulator"
              : health.ea_online
              ? "ea live"
              : "ea offline"}
          </span>
        )}
      </div>
      <div className="border border-zinc-800 bg-black/40 p-3 font-mono text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-zinc-400">State</span>
          <span className={status?.connected ? "text-emerald-400" : "text-red-400"}>
            {status?.connected ? "CONNECTED" : "OFFLINE"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-zinc-400">Account</span>
          <span className="text-zinc-300">{status?.account?.login || "—"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-zinc-400">Balance</span>
          <span className="text-zinc-300">
            {status?.account?.balance != null ? `$${status.account.balance}` : "—"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-zinc-400">Execution</span>
          <span className="text-zinc-300">
            {health?.ea_online ? "Live MT5 EA" : health?.mode === "simulator" ? "Simulator" : "EA offline"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-zinc-400">Last sync</span>
          <span className="text-zinc-300" data-testid="mt5-last-sync">
            {health?.synced_at
              ? `${Math.max(
                  0,
                  Math.round((Date.now() - new Date(health.synced_at).getTime()) / 1000)
                )}s ago`
              : "—"}
          </span>
        </div>
      </div>
      <div>
        <Label>Login</Label>
        <input
          data-testid="my-mt5-login-input"
          className={inputCls}
          value={creds.login}
          onChange={(e) => setCreds({ ...creds, login: e.target.value })}
        />
      </div>
      <div>
        <Label>Password</Label>
        <input
          data-testid="my-mt5-password-input"
          type="password"
          className={inputCls}
          value={creds.password}
          onChange={(e) => setCreds({ ...creds, password: e.target.value })}
        />
      </div>
      <div>
        <Label>Server</Label>
        <input
          data-testid="my-mt5-server-input"
          className={inputCls}
          value={creds.server}
          onChange={(e) => setCreds({ ...creds, server: e.target.value })}
        />
      </div>
      <div className="flex gap-2">
        <button
          data-testid="my-mt5-connect-btn"
          type="submit"
          disabled={busy}
          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95 disabled:opacity-50"
        >
          {busy ? "Connecting…" : "Connect"}
        </button>
        {status?.connected && (
          <button
            data-testid="my-mt5-disconnect-btn"
            type="button"
            onClick={disconnect}
            className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-4 rounded-sm text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-200"
          >
            Disconnect
          </button>
        )}
      </div>
    </form>
  );
};
